import React, { memo, useCallback, useMemo } from 'react';
import classnames from 'clsx';
import { useIntl } from 'react-intl';

import { FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import styles from './plan-details.module.less';
import { localizedPlanNameMessageDescriptor } from '../../plan-name-helper';
import { PlanDescription } from './plan-description/plan-description';
import { PlanHighlights } from './plan-highlights/plan-highlights';
import { PlanPrice } from './plan-price/plan-price';
import { PlanShipmentLimit } from './plan-shipment-limit/plan-shipment-limit';
import { PlanTag } from './plan-tag/plan-tag';
import { PlanAnalyticsLicenseCount } from './plan-analytics-license-count/plan-analytics-license-count';
import { PlanUserCount } from './plan-user-count/plan-user-count';
import { SelectPlanButton } from './select-plan-button/select-plan-button';
import {
  useGetPlanPrice,
  useShipmentLimitCostForBillingPlan,
  useAdditionalUserCountCostForBillingPlanId,
  useAdditionalAnalyticsLicenseCountCostForBillingPlanId,
} from '../../../hooks/use-plan-details';
import { useEffectivePlanDiscount } from '../../../hooks/use-coupon';
import { COUPON, PROMOTION } from './promotional-price/promotional-price';

/**
 * @typedef {import('../../../../../../prop-types/subscription-data-types.d.ts').BillingPlan} BillingPlan
 */

export const PlanDetails = memo(
  /**
   * @param {object} props - Component props
   * @param {number} props.additionalAnalyticsLicenseCount
   * @param {number} props.additionalUserCount
   * @param {boolean} props.isCurrentPlan - Whether the plan is the current plan
   * @param {boolean} props.isCurrentBillingPlanTrial - Whether this is the current billing plan trial
   * @param {boolean} props.isDeselectAllowed
   * @param {boolean} props.isRecommendedPlan - Whether the plan is recommended
   * @param {boolean} props.isSelectedPlan - Whether the plan is selected
   * @param {(billingPlanId: string, count: number) => void} props.onChangeAnalyticsLicenseCount
   * @param {(billingPlanId: string) => void} props.onSelectPlan - Callback function when the plan is selected
   * @param {BillingPlan} props.plan - The plan data
   * @param {string} props.previousTierPlanName - The name of the previous tier plan
   * @param {(billingPlanId: string, count: number) => void} props.onChangeSelectedShipmentLimit
   * @param {(billingPlanId: string, additionalUserCount: number) => void} props.onChangeUserCount
   * @param {number} props.selectedShipmentLimit - The selected shipment limit for this plan
   * @param {boolean} props.showAnnualPlans - Whether annual plans are being displayed
   * @param {number | undefined} props.promoMinEligibleVolume - Minimum volume for promo discount
   * @param {number | undefined} props.couponMinEligibleVolume - Minimum volume for coupon discount
   * @param {number | undefined} props.promoDiscountPercent - Promo discount percent
   * @param {number | undefined} props.couponDiscountPercent - Coupon discount percent
   * @returns {React.JSX.Element} The rendered component
   */
  ({
    additionalAnalyticsLicenseCount,
    additionalUserCount,
    isCurrentPlan,
    isCurrentBillingPlanTrial,
    isDeselectAllowed,
    isRecommendedPlan,
    isSelectedPlan,
    onChangeAnalyticsLicenseCount,
    onSelectPlan,
    plan,
    previousTierPlanName,
    onChangeSelectedShipmentLimit,
    onChangeUserCount,
    selectedShipmentLimit,
    showAnnualPlans,
  }) => {
    const { billingPlanId } = plan;
    const intl = useIntl();

    const planName = useMemo(() => {
      const { messageDescriptor, values } = localizedPlanNameMessageDescriptor(plan.name);

      return intl.formatMessage(messageDescriptor, values);
    }, [intl, plan.name]);

    const planBasePrice = useGetPlanPrice(plan);
    const shipmentLimitPrice = useShipmentLimitCostForBillingPlan(billingPlanId, selectedShipmentLimit);
    const shipmentLimitPriceValue = shipmentLimitPrice?.value || 0;
    const userCountPrice = useAdditionalUserCountCostForBillingPlanId(billingPlanId, additionalUserCount);
    const analyticsLicenseCountPrice = useAdditionalAnalyticsLicenseCountCostForBillingPlanId(
      billingPlanId,
      additionalAnalyticsLicenseCount ?? 0,
    );

    const planPriceWithSelectedOptions = useMemo(() => {
      return planBasePrice.value + shipmentLimitPriceValue + userCountPrice.value + analyticsLicenseCountPrice.value;
    }, [planBasePrice.value, shipmentLimitPriceValue, userCountPrice.value, analyticsLicenseCountPrice.value]);

    const {
      effectiveDiscount,
      isCouponDiscount,
      promoMinEligibleVolume,
      couponMinEligibleVolume,
      promoDiscountPercent,
      couponDiscountPercent,
    } = useEffectivePlanDiscount(plan.code, planPriceWithSelectedOptions, selectedShipmentLimit);
    const discountVariant = isCouponDiscount ? COUPON : PROMOTION;

    const handleSelectPlan = useCallback(() => {
      onSelectPlan(billingPlanId);
    }, [onSelectPlan, billingPlanId]);

    return (
      <div
        aria-label={planName}
        className={classnames(
          styles['plan-header'],
          { [styles['is-current']]: isCurrentPlan },
          { [styles['is-selected']]: !isCurrentPlan && isSelectedPlan },
        )}
      >
        <div className={styles['plan-info']}>
          <FlexRow className={styles['plan-name-row']}>
            <Text className={styles['plan-name']} textType={TextType.H3} value={planName} />
            <PlanTag isCurrentPlan={isCurrentPlan} isRecommendedPlan={isRecommendedPlan} isSelectedPlan={isSelectedPlan} />
          </FlexRow>
          <PlanDescription billingPlanId={billingPlanId} />
        </div>
        <div className={styles['shipment-limit-and-user-count']}>
          <PlanShipmentLimit
            billingPlanId={billingPlanId}
            onChange={onChangeSelectedShipmentLimit}
            selectedShipmentLimit={selectedShipmentLimit}
            promoMinEligibleVolume={promoMinEligibleVolume}
            couponMinEligibleVolume={couponMinEligibleVolume}
            promoDiscountPercent={promoDiscountPercent}
            couponDiscountPercent={couponDiscountPercent}
          />
          <PlanUserCount
            additionalUserCount={additionalUserCount}
            discountPercent={effectiveDiscount?.discountPercent}
            onChange={onChangeUserCount}
            plan={plan}
          />
          <PlanAnalyticsLicenseCount
            additionalLicenseCount={additionalAnalyticsLicenseCount ?? 0}
            discountPercent={effectiveDiscount?.discountPercent}
            onChange={onChangeAnalyticsLicenseCount}
            plan={plan}
          />
        </div>
        <div className={styles.divider} />
        <div className={styles['plan-current-price']}>
          <PlanPrice
            currencyCode={planBasePrice.code}
            discountVariant={discountVariant}
            effectiveDiscount={effectiveDiscount}
            isAnnual={showAnnualPlans}
            price={planPriceWithSelectedOptions}
          />
        </div>
        <SelectPlanButton
          isCurrentBillingPlanTrial={isCurrentBillingPlanTrial}
          isCurrentPlan={isCurrentPlan}
          isDeselectAllowed={isDeselectAllowed}
          isSelectedPlan={isSelectedPlan}
          onClick={handleSelectPlan}
        />
        <PlanHighlights
          billingPlanId={billingPlanId}
          isCurrentPlan={isCurrentPlan}
          planName={planName}
          previousTierPlanName={previousTierPlanName}
        />
      </div>
    );
  },
);
